import { Box } from "@mui/system";

import { useCallback, useEffect, useRef, useState } from "react";

import { Input, Stack, Typography, useTheme } from "@mui/material";

import { RestaurantHeader, useWindowDimensions } from "igfd-ui/src";

import { validateStaffAppPIN } from "igfd-commons/src/utils";

import Logo from "../../assets/logo.svg";
import { useRestaurantContext } from "../../hooks/useRestaurantContext";

const HEADER_HEIGHT = 320;

interface StaffLoginPageProps {
  onAuthenticate: () => void;
}

export const StaffLoginPage = ({ onAuthenticate }: StaffLoginPageProps) => {
  const [inputs, setInputs] = useState(["", "", "", ""]);
  const [error, setError] = useState<string>();
  const [isValidating, setIsValidating] = useState(false);
  const input1ref = useRef<HTMLInputElement>(null);
  const input2ref = useRef<HTMLInputElement>(null);
  const input3ref = useRef<HTMLInputElement>(null);
  const input4ref = useRef<HTMLInputElement>(null);

  const { brandName, logoImageUrl, hashedStaffAppPIN } = useRestaurantContext();
  const {
    palette: { oscar },
  } = useTheme();

  const { height } = useWindowDimensions();
  const contentCentered = height - HEADER_HEIGHT;
  const code = inputs.join("");

  const handleValidateCode = useCallback(async () => {
    if (isValidating) return;
    setIsValidating(true);
    setError(undefined);

    try {
      if (hashedStaffAppPIN) {
        const isValid = await validateStaffAppPIN(code, hashedStaffAppPIN);
        if (isValid) {
          onAuthenticate();
          return;
        }
        // PIN is invalid
        setError("Invalid passcode, try again.");
      } else {
        // No hashed PIN available
        setError("Access unavailable. Please contact an administrator.");
      }

      setInputs(["", "", "", ""]);
      input1ref.current?.focus();
    } catch (error) {
      console.error("Error validating PIN:", error);
      setError("Error validating passcode, please try again.");
      setInputs(["", "", "", ""]);
      input1ref.current?.focus();
    } finally {
      setIsValidating(false);
    }
  }, [code, onAuthenticate, hashedStaffAppPIN, isValidating]);

  useEffect(() => {
    if (code.length === 4 && !isValidating) {
      handleValidateCode();
    }
  }, [code, handleValidateCode, isValidating]);

  useEffect(() => {
    input1ref.current?.focus();
  }, []);

  const handleInputChange = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => {
    const matches = /([0-9])/.exec(e?.key || "");
    const isBackspace = e?.key === "Backspace";

    if (!isBackspace && !matches) {
      return;
    }

    setInputs((prev) => {
      const newValues = [...prev];
      newValues[index] = isBackspace ? "" : e?.key || "";
      return newValues;
    });

    switch (index) {
      case 0:
        if (!isBackspace) {
          input2ref.current?.focus();
        }
        break;
      case 1:
        if (isBackspace) {
          input1ref.current?.focus();
        } else {
          input3ref.current?.focus();
        }
        break;
      case 2:
        if (isBackspace) {
          input2ref.current?.focus();
        } else {
          input4ref.current?.focus();
        }
        break;
      case 3:
        if (isBackspace) {
          input3ref.current?.focus();
        }
        break;
      default:
        break;
    }
  };

  return (
    <Stack
      flex={1}
      sx={({ palette }) => ({ backgroundColor: palette.background.paper })}
    >
      <Box marginTop={10} marginBottom={4}>
        <RestaurantHeader brandName={brandName} logoImageUrl={logoImageUrl} />
      </Box>

      <Stack
        display="flex"
        gap={8}
        alignItems="center"
        justifyContent="center"
        height={contentCentered}
      >
        <Typography variant="headingLLight" align="center">
          {`Hi, welcome to your ${brandName}`}
          <br />
          Staff Link
        </Typography>

        <Stack>
          <Typography variant="labelM" align="center">
            Enter passcode
          </Typography>

          <Stack direction="row" gap={2} justifyContent="center" marginTop={2}>
            <Input
              inputRef={input1ref}
              disableUnderline
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
              }}
              onKeyDown={(e) => handleInputChange(0, e)}
              sx={{
                border: `1px solid ${oscar.light.borders.gray}`,
                borderRadius: 2,
                padding: 2,
                height: 48,
                width: 48,
              }}
              value={inputs[0]}
            />
            <Input
              inputRef={input2ref}
              disableUnderline
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
              }}
              onKeyDown={(e) => handleInputChange(1, e)}
              sx={{
                border: `1px solid ${oscar.light.borders.gray}`,
                borderRadius: 2,
                padding: 2,
                height: 48,
                width: 48,
              }}
              value={inputs[1]}
            />
            <Input
              inputRef={input3ref}
              disableUnderline
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
              }}
              onKeyDown={(e) => handleInputChange(2, e)}
              sx={{
                border: `1px solid ${oscar.light.borders.gray}`,
                borderRadius: 2,
                padding: 2,
                height: 48,
                width: 48,
              }}
              value={inputs[2]}
            />
            <Input
              inputRef={input4ref}
              disableUnderline
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
              }}
              onKeyDown={(e) => handleInputChange(3, e)}
              sx={{
                border: `1px solid ${oscar.light.borders.gray}`,
                borderRadius: 2,
                padding: 2,
                height: 48,
                width: 48,
              }}
              value={inputs[3]}
            />
          </Stack>
          <Stack paddingTop={4}>
            {error ? (
              <Typography
                variant="labelS"
                color={oscar.light.content.danger}
                textAlign="center"
              >
                {error}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Stack>

      <Stack
        position="absolute"
        bgcolor={oscar.light.surface.clear}
        left={0}
        right={0}
        alignItems="center"
        paddingTop={1}
        bottom={40}
      >
        <img src={Logo} alt="Ingredifind" style={{ width: 130 }} />
      </Stack>
    </Stack>
  );
};
